import React, { useState, useEffect, useCallback } from 'react';
import {
    Modal, Form, Input, Select, Button, Tabs, Table, Space,
    Typography, Tag, notification, Divider, InputNumber, Row, Col,
    Tooltip, Popconfirm,
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { addVariable, updateVariable } from '../../../api/payments';
import { BOOKING_FIELDS } from '../../FinanceEngine/constants/booking.constants';
import { OPTIONS as OPERATORS } from '../../FinanceEngine/constants/common.constants';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface VariableEditorModalProps {
  visible: boolean;
  variable: any;
  onClose: (updatedVariable?: any) => void;
}

type ValueType = 'simple' | 'condition' | 'lookup';

const detectValueType = (value: string): ValueType => {
    if (!value) return 'simple';
    const strVal = String(value);
    if (strVal.startsWith('{') && strVal.includes(':')) {
        try {
            JSON.parse(strVal);
            return 'lookup';
        } catch {
            return 'simple';
        }
    }
    if (strVal.includes('{{booking.') || strVal.includes('===') || strVal.includes('!==')) return 'condition';
    return 'simple';
};

// Lookup editor: table per country with key-value pairs
interface LookupEntry {
  key: string;
  method: string;
  value: number | string;
}

interface LookupCountry {
  country: string;
  entries: LookupEntry[];
}

const parseLookupValue = (value: string): LookupCountry[] => {
    try {
        const parsed = JSON.parse(value);
        return Object.entries(parsed).map(([country, methods]: [string, any]) => ({
            country,
            entries: Object.entries(methods).map(([method, val]) => ({
                key: `${country}-${method}`,
                method,
                value: val as number | string,
            })),
        }));
    } catch {
        return [];
    }
};

const buildLookupString = (countries: LookupCountry[]): string => {
    const obj: Record<string, Record<string, number | string>> = {};
    countries.forEach(({ country, entries }) => {
        obj[country] = {};
        entries.forEach(({ method, value }) => {
            obj[country][method] = typeof value === 'string' ? (Number(value) || value) : value;
        });
    });
    return JSON.stringify(obj);
};

// Condition builder
interface ConditionParts {
  field: string;
  operator: string;
  compareValue: string;
}

const parseCondition = (value: string): ConditionParts => {
    const strVal = String(value);
    // Pattern: "{{booking.field}}"==="value" or "{{booking.field}}"!=="value"
    const match = strVal.match(/\{\{booking\.([^}]+)\}\}['"]\s*(===|!==|==|!=|>=|<=|>|<)\s*['""]?([^"']*)['""]?/);
    if (match) {
        const operatorMap: Record<string, string> = {
            '===': 'EQUALS',
            '==': 'EQUALS',
            '!==': 'NOT_EQUALS',
            '!=': 'NOT_EQUALS',
            '>': 'GREATER_THAN',
            '<': 'LESS_THAN',
            '>=': 'GREATER_THAN_OR_EQUALS',
            '<=': 'LESS_THAN_OR_EQUALS',
        };
        return {
            field: match[1],
            operator: operatorMap[match[2]] || 'EQUALS',
            compareValue: match[3],
        };
    }
    return { field: '', operator: 'EQUALS', compareValue: '' };
};

const buildConditionString = (parts: ConditionParts): string => {
    const operatorMap: Record<string, string> = {
        EQUALS: '===',
        NOT_EQUALS: '!==',
        GREATER_THAN: '>',
        LESS_THAN: '<',
        GREATER_THAN_OR_EQUALS: '>=',
        LESS_THAN_OR_EQUALS: '<=',
    };
    const jsOp = operatorMap[parts.operator] || '===';
    return `"{{booking.${parts.field}}}"${jsOp}"${parts.compareValue}"`;
};

const VariableEditorModal: React.FC<VariableEditorModalProps> = ({
    visible, variable, onClose,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [valueType, setValueType] = useState<ValueType>('simple');

    // Simple value state
    const [simpleValue, setSimpleValue] = useState<string>('');

    // Condition state
    const [conditionParts, setConditionParts] = useState<ConditionParts>({
        field: '', operator: 'EQUALS', compareValue: '',
    });

    // Lookup state
    const [lookupCountries, setLookupCountries] = useState<LookupCountry[]>([]);
    const [newCountryName, setNewCountryName] = useState('');

    const isEditing = !!variable?._id;

    useEffect(() => {
        if (!variable) return;

        form.setFieldsValue({
            name: variable.name || '',
            origin: variable.origin || 'BOOKING',
            type: variable.type || 'FIXED',
        });

        const val = String(variable.value || '');
        const detected = detectValueType(val);
        setValueType(detected);

        switch (detected) {
        case 'lookup':
            setLookupCountries(parseLookupValue(val));
            break;
        case 'condition':
            setConditionParts(parseCondition(val));
            break;
        default:
            setSimpleValue(val);
        }
    }, [variable, form]);

    const buildFinalValue = (): string => {
        switch (valueType) {
        case 'lookup':
            return buildLookupString(lookupCountries);
        case 'condition':
            return buildConditionString(conditionParts);
        default:
            return simpleValue;
        }
    };

    const handleSave = async () => {
        try {
            await form.validateFields();
        } catch {
            return;
        }

        setLoading(true);
        const values = form.getFieldsValue();
        const keyName = values.name.toUpperCase().replace(/\s+/g, '_');
        const finalValue = buildFinalValue();

        const payload = {
            ...values,
            value: finalValue,
            planID: variable?.planID || undefined,
            keyName,
        };

        try {
            if (isEditing) {
                const { data } = await updateVariable(variable._id, payload);
                notification.success({ message: 'Variable updated' });
                onClose(data.data);
            } else {
                const { data } = await addVariable(payload);
                notification.success({ message: 'Variable created' });
                onClose(data.data);
            }
        } catch (err) {
            notification.error({ message: 'Error saving variable' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // Lookup handlers
    const addCountry = () => {
        if (!newCountryName.trim()) return;
        if (lookupCountries.some((c) => c.country === newCountryName.toUpperCase())) {
            notification.warning({ message: 'Country already exists' });
            return;
        }
        setLookupCountries([
            ...lookupCountries,
            { country: newCountryName.toUpperCase(), entries: [{ key: `${newCountryName}-default`, method: '0', value: 0 }] },
        ]);
        setNewCountryName('');
    };

    const removeCountry = (country: string) => {
        setLookupCountries(lookupCountries.filter((c) => c.country !== country));
    };

    const addEntry = (countryIdx: number) => {
        const updated = [...lookupCountries];
        const newKey = `entry-${Date.now()}`;
        updated[countryIdx].entries.push({ key: newKey, method: '', value: 0 });
        setLookupCountries(updated);
    };

    const updateEntry = (countryIdx: number, entryIdx: number, field: 'method' | 'value', val: any) => {
        const updated = [...lookupCountries];
        (updated[countryIdx].entries[entryIdx] as any)[field] = val;
        setLookupCountries(updated);
    };

    const removeEntry = (countryIdx: number, entryIdx: number) => {
        const updated = [...lookupCountries];
        updated[countryIdx].entries.splice(entryIdx, 1);
        setLookupCountries(updated);
    };

    const renderSimpleEditor = () => (
        <div>
            <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
                Enter a numeric or text value
            </Text>
            <Input
                placeholder="e.g., 10.5"
                value={simpleValue}
                onChange={(e) => setSimpleValue(e.target.value)}
                size="large"
                style={{ fontFamily: 'monospace' }}
            />
        </div>
    );

    const renderConditionEditor = () => (
        <div>
            <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                Build a condition that evaluates a booking field
            </Text>
            <Row gutter={12}>
                <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Booking Field</Text>
                    <Select
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder="Select field"
                        showSearch
                        optionFilterProp="children"
                        value={conditionParts.field || undefined}
                        onChange={(val) => setConditionParts({ ...conditionParts, field: val })}
                    >
                        {BOOKING_FIELDS.map((f) => (
                            <Select.Option key={f.value} value={f.value}>{f.label}</Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Operator</Text>
                    <Select
                        style={{ width: '100%', marginTop: 4 }}
                        value={conditionParts.operator}
                        onChange={(val) => setConditionParts({ ...conditionParts, operator: val })}
                    >
                        {OPERATORS.map((o) => (
                            <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col span={8}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Compare Value</Text>
                    <Input
                        style={{ marginTop: 4 }}
                        placeholder="e.g., SMALL_FLEET"
                        value={conditionParts.compareValue}
                        onChange={(e) => setConditionParts({ ...conditionParts, compareValue: e.target.value })}
                    />
                </Col>
            </Row>
            <div style={{
                marginTop: 16, padding: 12, background: '#1f1f1f', borderRadius: 6,
            }}
            >
                <Text type="secondary" style={{ fontSize: 11 }}>GENERATED VALUE:</Text>
                <Paragraph code copyable style={{ marginTop: 4, marginBottom: 0 }}>
                    {buildConditionString(conditionParts)}
                </Paragraph>
            </div>
        </div>
    );

    const renderLookupEditor = () => (
        <div>
            <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                Define values per country and key (payment method, provider, etc.)
            </Text>

            <Space style={{ marginBottom: 16 }}>
                <Input
                    placeholder="Country code (e.g., EC, MX, CL)"
                    value={newCountryName}
                    onChange={(e) => setNewCountryName(e.target.value)}
                    onPressEnter={addCountry}
                    style={{ width: 250 }}
                />
                <Button icon={<PlusOutlined />} onClick={addCountry}>
                    Add Country
                </Button>
            </Space>

            {lookupCountries.map((country, cIdx) => (
                <div
                    key={country.country}
                    style={{
                        marginBottom: 16,
                        border: '1px solid #303030',
                        borderRadius: 8,
                        overflow: 'hidden',
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 16px',
                        background: '#1f1f1f',
                    }}
                    >
                        <Tag color="blue" style={{ fontSize: 14 }}>{country.country}</Tag>
                        <Space>
                            <Button
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => addEntry(cIdx)}
                            >
                                Add Key
                            </Button>
                            <Popconfirm
                                title={`Remove ${country.country}?`}
                                onConfirm={() => removeCountry(country.country)}
                            >
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </Space>
                    </div>
                    <Table
                        size="small"
                        dataSource={country.entries}
                        rowKey="key"
                        pagination={false}
                        columns={[
                            {
                                title: 'Key',
                                dataIndex: 'method',
                                width: '50%',
                                render: (val: string, _: any, eIdx: number) => (
                                    <Input
                                        size="small"
                                        value={val}
                                        placeholder="e.g., CASH, CARD, 0"
                                        onChange={(e) => updateEntry(cIdx, eIdx, 'method', e.target.value)}
                                    />
                                ),
                            },
                            {
                                title: 'Value',
                                dataIndex: 'value',
                                width: '40%',
                                render: (val: any, _: any, eIdx: number) => (
                                    <InputNumber
                                        size="small"
                                        value={val}
                                        style={{ width: '100%' }}
                                        onChange={(v) => updateEntry(cIdx, eIdx, 'value', v || 0)}
                                    />
                                ),
                            },
                            {
                                title: '',
                                width: 50,
                                render: (_: any, __: any, eIdx: number) => (
                                    <Button
                                        size="small"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeEntry(cIdx, eIdx)}
                                    />
                                ),
                            },
                        ]}
                    />
                </div>
            ))}

            {lookupCountries.length > 0 && (
                <div style={{
                    marginTop: 12, padding: 12, background: '#1f1f1f', borderRadius: 6,
                }}
                >
                    <Text type="secondary" style={{ fontSize: 11 }}>GENERATED VALUE:</Text>
                    <Paragraph
                        code
                        copyable
                        ellipsis={{ rows: 2, expandable: true }}
                        style={{ marginTop: 4, marginBottom: 0, fontSize: 11 }}
                    >
                        {buildLookupString(lookupCountries)}
                    </Paragraph>
                </div>
            )}
        </div>
    );

    return (
        <Modal
            title={isEditing ? `Edit: ${variable?.name}` : 'Create Variable'}
            open={visible}
            width={800}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>Cancel</Button>,
                <Button key="save" type="primary" loading={loading} onClick={handleSave}>
                    {isEditing ? 'Update' : 'Create'}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" initialValues={{ type: 'FIXED', origin: 'BOOKING' }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Name"
                            name="name"
                            rules={[{ required: true, message: 'Name is required' }]}
                        >
                            <Input placeholder="e.g., Payment Fee" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label="Used for" name="origin">
                            <Select>
                                <Select.Option value="BOOKING">Booking</Select.Option>
                                <Select.Option value="MRR">MRR</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label="Type" name="type">
                            <Select>
                                <Select.Option value="FIXED">Fixed</Select.Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ marginBottom: 12 }}>
                <Text strong>Value Type</Text>
                <Tooltip title="Choose the type of value this variable will hold">
                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#8c8c8c' }} />
                </Tooltip>
            </div>

            <Tabs
                activeKey={valueType}
                onChange={(key) => setValueType(key as ValueType)}
                size="small"
            >
                <TabPane tab="Simple Value" key="simple" />
                <TabPane tab="Condition" key="condition" />
                <TabPane tab="Lookup Table" key="lookup" />
            </Tabs>

            <div style={{ marginTop: 16 }}>
                {valueType === 'simple' && renderSimpleEditor()}
                {valueType === 'condition' && renderConditionEditor()}
                {valueType === 'lookup' && renderLookupEditor()}
            </div>
        </Modal>
    );
};

export { VariableEditorModal };
